import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ShieldCheck, Database, Server, Trash2, Download, Lock } from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <section className="border border-white/10 rounded-sm bg-secondary p-6" data-testid={`privacy-section-${title}`}>
    <h2 className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight mb-3">
      <Icon className="h-5 w-5 text-accent" /> {title}
    </h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background grain">
      <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 lg:px-10">
        <Logo size={32} textClassName="font-heading font-black tracking-[0.14em] uppercase text-base bg-gradient-to-br from-[#FDE7A8] via-[#E8B527] to-[#B8860B] bg-clip-text text-transparent" />
        <Link
          to="/login"
          data-testid="privacy-login-link"
          className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground hover:text-foreground transition-colors"
        >
          Accès sécurisé
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-accent mb-3">Privacy by Design</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mb-4">
            Politique de confidentialité
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            <b className="text-foreground">NATYE est actuellement une application professionnelle privée dont l'accès est réservé aux utilisateurs expressément autorisés.</b>{" "}
            Nous appliquons le principe de <b>minimisation des données</b> : nous ne collectons que le
            strict nécessaire au fonctionnement du service. Cette page est informative et ne constitue
            ni une certification, ni une garantie de conformité. Dernière mise à jour : 2026.
          </p>
        </div>

        <Section icon={ShieldCheck} title="Exploitant & responsable du traitement">
          <p>Ces informations seront complétées lorsque l'activité sera officiellement immatriculée :</p>
          <ul className="list-none pl-0 space-y-1">
            {[
              "Exploitant / raison sociale",
              "Statut juridique",
              "Adresse professionnelle",
              "SIREN / SIRET",
              "E-mail professionnel de contact",
              "Hébergeur",
              "Responsable du traitement",
            ].map((f) => (
              <li key={f} className="flex justify-between gap-4 border-b border-white/5 py-1">
                <span>{f}</span>
                <span className="text-accent/80 shrink-0">À compléter</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Database} title="Données traitées">
          <p>Nous traitons uniquement :</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Comptes utilisateurs : e-mail et mot de passe (chiffré, jamais en clair).</li>
            <li>Fiches marques : nom, @Instagram, site source, univers de marque, couleur.</li>
            <li>Contenus créés : produits importés, légendes, hashtags, visuels générés, posts planifiés.</li>
            <li>Le cas échéant, un token d'accès Instagram (chiffré au repos).</li>
          </ul>
          <p>Aucun traceur publicitaire, aucun cookie marketing, aucune revente de données.</p>
        </Section>

        <Section icon={ShieldCheck} title="Finalités">
          <p>
            Les données servent exclusivement à créer, planifier et publier des posts pour les marques
            gérées dans l'outil, et à générer des textes/visuels via l'IA.
          </p>
        </Section>

        <Section icon={Server} title="Sous-traitants (fournisseurs externes)">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Emergent (IA)</b> — génération de légendes, hashtags et visuels (OpenAI / Google Gemini).</li>
            <li><b>MongoDB</b> — hébergement de la base de données applicative.</li>
            <li><b>Object Storage</b> — stockage des visuels générés par l'IA.</li>
            <li><b>Meta / Instagram Graph API</b> — publication (uniquement si un compte est connecté).</li>
          </ul>
        </Section>

        <Section icon={Lock} title="Sécurité">
          <p>
            Accès strictement privé (compte administrateur unique), mots de passe chiffrés (bcrypt),
            protection contre les attaques par force brute, tokens de session révocables à tout moment,
            secrets (tokens Instagram) chiffrés au repos.
          </p>
        </Section>

        <Section icon={Download} title="Vos droits (RGPD)">
          <p>
            Vous disposez d'un droit d'accès, de rectification, d'effacement et de portabilité.
            Depuis l'outil, vous pouvez à tout moment <b>exporter</b> l'ensemble des données d'une marque
            (format JSON) et <b>supprimer définitivement</b> une marque et toutes ses données associées.
          </p>
        </Section>

        <Section icon={Trash2} title="Conservation & suppression">
          <p>
            Les données sont conservées le temps de l'utilisation du service. Les visuels et posts sont
            soumis à une durée de conservation paramétrable. La suppression est <b>définitive</b> et
            irréversible (base de données et stockage des fichiers).
          </p>
        </Section>

        <p className="text-xs text-muted-foreground pt-4 border-t border-white/10">
          Pour toute demande relative à vos données, contactez l'administrateur de l'espace NATYE.
        </p>
      </main>
    </div>
  );
}
